import { SenderWarningBanner } from './SenderWarning';
// src/components/Settings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import PrivateListingForm from './PrivateListingForm';
import './Settings.css';

const Settings = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [contactValue, setContactValue] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelText, setCancelText] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [pageViews, setPageViews] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [issueType, setIssueType] = useState('Site Issue');
  const [issueDescription, setIssueDescription] = useState('');
  const [showIssueSuccessModal, setShowIssueSuccessModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyType, setModifyType] = useState('page_copy_change');
  const [modifyDescription, setModifyDescription] = useState('');
  const [modifyTargetPage, setModifyTargetPage] = useState('');
  const [modifyTargetAgent, setModifyTargetAgent] = useState('');
  const [modifyPriority, setModifyPriority] = useState('medium');
  const [showModifySuccessModal, setShowModifySuccessModal] = useState(false);
  const [showPrivateListingForm, setShowPrivateListingForm] = useState(false);
  const [showListingSuccessModal, setShowListingSuccessModal] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
    fetchPageViews();
  }, []);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setEmailValue(data.agentEmail || '');
        setContactValue(data.agentContact || '');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/comm/templates?siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleChatLeadTemplateSelect = async (templateName) => {
    await updateSettings({ chatleadEmailTemplate: templateName });
  };

  const handleSignupTemplateSelect = async (templateName) => {
    await updateSettings({ signupEmailTemplate: templateName });
  };

  const fetchPageViews = async () => {
    try {
      const response = await fetch(`https://tophamtechbeb.com/aw-monitor/v1/page-views?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPageViews(data);
      }
    } catch (error) {
      console.error('Error fetching page views:', error);
    }
  };

  const updateSettings = async (updatedFields) => {
    try {
      const params = new URLSearchParams({
        siteId: SITE_CONFIG.SITE_ID,
        ...updatedFields
      });
      
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?${params}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const updateEmail = async () => {
    await updateSettings({ agentEmail: emailValue });
    setEditingEmail(false);
  };

  const updateContact = async () => {
    await updateSettings({ agentContact: contactValue });
    setEditingContact(false);
  };

  const submitModifyRequest = async () => {
    if (!modifyDescription.trim()) {
      alert('Please describe the change you would like.');
      return;
    }

    try {
      const payload = {
        customer_id: SITE_CONFIG.SITE_ID,
        site_id: SITE_CONFIG.SITE_ID,
        request_type: modifyType,
        description: modifyDescription,
        priority: modifyPriority
      };
      if (modifyTargetPage.trim()) payload.target_page = modifyTargetPage.trim();
      if (modifyTargetAgent.trim()) payload.target_agent = modifyTargetAgent.trim();

      const response = await fetch('https://tophamtechbeb.com/aw-monitor/v1/site-modify-req', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowModifyModal(false);
        setModifyType('page_copy_change');
        setModifyDescription('');
        setModifyTargetPage('');
        setModifyTargetAgent('');
        setModifyPriority('medium');
        setShowModifySuccessModal(true);
      } else {
        alert('Failed to submit the request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting site modify request:', error);
      alert('Failed to submit the request. Please try again.');
    }
  };

  const handleReportIssue = () => {
    setShowReportModal(true);
  };

  const submitIssueReport = async () => {
    if (!issueDescription.trim()) {
      alert('Please provide a description of the issue.');
      return;
    }

    try {
      const response = await fetch('https://tophamtechbeb.com/aw-monitor/v1/incidents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          incident_type: issueType,
          description: issueDescription,
          incident_date: new Date().toISOString().split('T')[0]
        })
      });
      
      if (response.ok) {
        setShowReportModal(false);
        setIssueType('Site Issue');
        setIssueDescription('');
        setShowIssueSuccessModal(true);
      } else {
        alert('Failed to report issue. Please try again.');
      }
    } catch (error) {
      console.error('Error reporting issue:', error);
      alert('Failed to report issue. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    if (cancelText !== 'Cancel Subscription') {
      alert('Please type "Cancel Subscription" exactly as shown.');
      return;
    }

    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/cancel-subscription?siteId=${SITE_CONFIG.SITE_ID}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        setShowCancelModal(false);
        setCancelText('');
        setShowSuccessModal(true);
        fetchSettings(); // Refresh settings
      } else {
        alert('Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    }
  };

  const chatLeadTemplates = templates.filter((template) => (
    !template.scope || String(template.scope).toLowerCase() === 'brokerage'
  ));

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className={`settings ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="tools-header">
        <h1>Settings</h1>
        <div className="header-buttons">
          <button onClick={() => navigate('/admin')} className="back-button">
            Back to Admin
          </button>
          <button onClick={() => setShowModifyModal(true)} className="report-issue-button">
            Request a Change
          </button>
          <button onClick={handleReportIssue} className="report-issue-button">
            Report an Issue
          </button>
        </div>
      </div>

      <SenderWarningBanner />

      <div className="settings-container">
        <div className="settings-section">
          <h2>Toggle Settings</h2>
          
          <div className="setting-item">
            <div className="setting-info">
              <label>Chat Lead Text</label>
              <p>Enable text notifications for chat leads</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.chatleadText || false}
                onChange={(e) => updateSettings({ chatleadText: e.target.checked })}
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Open Chat</label>
              <p>Allow users to chat without signing in or selecting a service</p>
              <p style={{color: '#ff9800', fontSize: '12px', marginTop: '5px'}}>Toggling this ON may result in fewer chat leads</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.openChat || false}
                onChange={(e) => updateSettings({ openChat: e.target.checked })}
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Automated Email Followup</label>
              <p>Automatically send follow-up emails to leads</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.automatedEmailFollowup || false}
                onChange={(e) => {
                  updateSettings({ automatedEmailFollowup: e.target.checked });
                  setShowTemplateSelector(e.target.checked);
                }}
              />
              <span className="slider"></span>
            </div>
          </div>

          {(settings?.automatedEmailFollowup || showTemplateSelector) && (
            <div className="template-selector-container">
              <div className="template-selector-header">
                <label>Immediate Chat Lead Email Template</label>
                <p>Send this email immediately when a new chat lead is captured</p>
              </div>
              <div className="template-dropdown">
                <select
                  value={settings?.chatleadEmailTemplate || ''}
                  onChange={(e) => handleChatLeadTemplateSelect(e.target.value)}
                  className="template-select"
                >
                  <option value="" disabled>Select a template...</option>
                  {chatLeadTemplates.map((template) => (
                    <option key={template.id} value={template.templateName}>
                      {template.templateName}
                    </option>
                  ))}
                </select>
              </div>
              {settings?.chatleadEmailTemplate && (
                <div className="selected-template">
                  <span>Selected: {settings.chatleadEmailTemplate}</span>
                </div>
              )}

              <div className="template-selector-header" style={{marginTop: '20px'}}>
                <label>Signup Email Template</label>
                <p>Select the template to use for new user signup emails</p>
              </div>
              <div className="template-dropdown">
                <select
                  value={settings?.signupEmailTemplate || ''}
                  onChange={(e) => handleSignupTemplateSelect(e.target.value)}
                  className="template-select"
                >
                  <option value="">Select a template...</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.templateName}>
                      {template.templateName}
                    </option>
                  ))}
                </select>
              </div>
              {settings?.signupEmailTemplate && (
                <div className="selected-template">
                  <span>Selected: {settings.signupEmailTemplate}</span>
                </div>
              )}
            </div>
          )}

          <div className="setting-item">
            <div className="setting-info">
              <label>User Signup Text</label>
              <p>Show signup prompt text to users</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.userSignupText || false}
                onChange={(e) => updateSettings({ userSignupText: e.target.checked })}
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Digital Marketing</label>
              <p>Enable digital marketing campaigns</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.digitalMarketing || false}
                disabled
              />
              <span className="slider disabled"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Mail Marketing</label>
              <p>Enable physical mail marketing campaigns</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.mailMarketing || false}
                disabled
              />
              <span className="slider disabled"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Active</label>
              <p>Account active status</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.active || false}
                disabled
              />
              <span className="slider disabled"></span>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>Account Information</h2>
          
          <div className="info-item">
            <label>Plan Status</label>
            <div className="info-value">
              <span className="plan-status">{settings?.paid || 'Free'}</span>
            </div>
          </div>

          <div className="info-item">
            <label>Agent Email</label>
            <div className="info-value">
              {editingEmail ? (
                <div className="edit-field">
                  <input
                    type="email"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    className="edit-input"
                  />
                  <button onClick={updateEmail} className="save-btn">✓</button>
                  <button onClick={() => setEditingEmail(false)} className="cancel-btn">✕</button>
                </div>
              ) : (
                <div className="display-field">
                  <span>{settings?.agentEmail || 'Not set'}</span>
                  <button onClick={() => setEditingEmail(true)} className="edit-btn">✏️</button>
                </div>
              )}
            </div>
          </div>

          <div className="info-item">
            <label>Agent Contact</label>
            <div className="info-value">
              {editingContact ? (
                <div className="edit-field">
                  <input
                    type="tel"
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                    className="edit-input"
                  />
                  <button onClick={updateContact} className="save-btn">✓</button>
                  <button onClick={() => setEditingContact(false)} className="cancel-btn">✕</button>
                </div>
              ) : (
                <div className="display-field">
                  <span>{settings?.agentContact || 'Not set'}</span>
                  <button onClick={() => setEditingContact(true)} className="edit-btn">✏️</button>
                </div>
              )}
            </div>
          </div>

          <div className="cancel-section">
            <button 
              onClick={() => setShowCancelModal(true)}
              className="cancel-subscription-btn"
            >
              Cancel Subscription
            </button>
          </div>

          <div className="analytics-section">
            <h3>Site Analytics</h3>
            {pageViews ? (
              <div className="analytics-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Page Views:</span>
                  <span className="stat-value">{pageViews.count}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Mobile Visits:</span>
                  <span className="stat-value">{pageViews.page_views?.filter(pv => pv.is_mobile).length || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Unique Sessions:</span>
                  <span className="stat-value">{new Set(pageViews.page_views?.map(pv => pv.session_id)).size || 0}</span>
                </div>
              </div>
            ) : (
              <div className="loading-analytics">Loading analytics...</div>
            )}
          </div>
        </div>
      </div>

      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription</h3>
            <p>This action cannot be undone. To confirm, please type "Cancel Subscription" below:</p>
            <input
              type="text"
              value={cancelText}
              onChange={(e) => setCancelText(e.target.value)}
              placeholder="Type: Cancel Subscription"
              className="cancel-input"
            />
            <div className="modal-buttons">
              <button 
                onClick={handleCancelSubscription}
                className="confirm-cancel-btn"
                disabled={cancelText !== 'Cancel Subscription'}
              >
                Confirm Cancellation
              </button>
              <button 
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelText('');
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Subscription Cancelled</h3>
            <p>Your subscription has been successfully cancelled. You will retain access until the end of your current billing period.</p>
            <div className="modal-buttons">
              <button 
                onClick={() => setShowSuccessModal(false)}
                className="success-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showModifyModal && (
        <div className="modal-overlay" onClick={() => setShowModifyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Request a Site Change</h3>
            <p>Tell us what you'd like changed on your site:</p>

            <label>Change Type:</label>
            <select
              value={modifyType}
              onChange={(e) => setModifyType(e.target.value)}
              className="issue-select"
            >
              <option value="page_copy_change">Update page text</option>
              <option value="image_change">Swap or update images</option>
              <option value="agent_about_change">Update an agent bio</option>
              <option value="add_agent">Add an agent</option>
              <option value="remove_agent">Remove an agent</option>
              <option value="new_page">Add a new page</option>
              <option value="page_refactor">Redesign a page</option>
              <option value="feature_request">New feature</option>
              <option value="other">Other</option>
            </select>

            <label>Description:</label>
            <textarea
              value={modifyDescription}
              onChange={(e) => setModifyDescription(e.target.value)}
              placeholder="Describe the change - the more detail, the better..."
              className="issue-textarea"
              rows="4"
            />

            <label>Page (optional):</label>
            <input
              value={modifyTargetPage}
              onChange={(e) => setModifyTargetPage(e.target.value)}
              placeholder="e.g. /contact or homepage"
              className="issue-select"
            />

            <label>Agent (optional):</label>
            <input
              value={modifyTargetAgent}
              onChange={(e) => setModifyTargetAgent(e.target.value)}
              placeholder="Agent name, if this is about a team member"
              className="issue-select"
            />

            <label>Priority:</label>
            <select
              value={modifyPriority}
              onChange={(e) => setModifyPriority(e.target.value)}
              className="issue-select"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>

            <div className="modal-buttons">
              <button onClick={submitModifyRequest} className="submit-issue-btn">
                Submit Request
              </button>
              <button
                onClick={() => setShowModifyModal(false)}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showModifySuccessModal && (
        <div className="modal-overlay" onClick={() => setShowModifySuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Change Request Submitted</h3>
            <p>Thanks! Your request is in our queue. Most changes are completed within 1-2 business days.</p>
            <div className="modal-buttons">
              <button
                onClick={() => setShowModifySuccessModal(false)}
                className="success-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Report an Issue</h3>
            <p>Please describe the issue you're experiencing:</p>
            
            <label>Issue Type:</label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className="issue-select"
            >
              <option value="Site Issue">Site Issue</option>
              <option value="Site Down">Site Down</option>
              <option value="Performance Issue">Performance Issue</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Bug Report">Bug Report</option>
              <option value="Other">Other</option>
            </select>
            
            <label>Description:</label>
            <textarea
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              placeholder="Please provide details about the issue..."
              className="issue-textarea"
              rows="4"
            />
            
            <div className="modal-buttons">
              <button onClick={submitIssueReport} className="submit-issue-btn">
                Submit Report
              </button>
              <button 
                onClick={() => {
                  setShowReportModal(false);
                  setIssueType('Site Issue');
                  setIssueDescription('');
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showIssueSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowIssueSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Issue Reported Successfully</h3>
            <p>Thank you for reporting the issue. Our team has been notified and will investigate promptly.</p>
            <div className="modal-buttons">
              <button 
                onClick={() => setShowIssueSuccessModal(false)}
                className="success-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <button 
        className="add-private-listing-btn"
        onClick={() => setShowPrivateListingForm(true)}
      >
        + Add Private Listing
      </button>

      <PrivateListingForm 
        show={showPrivateListingForm}
        onClose={() => setShowPrivateListingForm(false)}
        onSuccess={() => {
          setShowPrivateListingForm(false);
          setShowListingSuccessModal(true);
        }}
      />

      {showListingSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowListingSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Listing Added Successfully</h3>
            <p>Your private listing has been added to the system.</p>
            <div className="modal-buttons">
              <button onClick={() => setShowListingSuccessModal(false)} className="success-close-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;